import { useEffect, useMemo, useState } from 'react'
import type { CruiseDeparturePriceTotals } from 'lib/cruises/cruiseDeparturePricing'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import {
  type CruiseDepartureSort,
  type DeparturesByDate,
  getCruiseDepartureCountByYear,
  getCruiseDepartureList,
  getCruiseDepartureYearOptions,
  groupCruiseDeparturesByMonth,
  sortCruiseDepartureList,
} from 'lib/cruises/cruiseDepartures'

interface Options {
  departuresByDate: DeparturesByDate
  lowestDeparturePricesByCategory: CruiseDeparturePriceTotals
}

export type { CruiseDepartureSort, DeparturesByDate } from 'lib/cruises/cruiseDepartures'

export default function useCruiseDeparturesModel({
  departuresByDate,
  lowestDeparturePricesByCategory,
}: Options) {
  const dateFormatter = useIntlDateFormatter()
  const [selectedYear, setSelectedYear] = useState<number | undefined>()
  const [sort, setSort] = useState<CruiseDepartureSort | undefined>()

  const departureList = useMemo(() => getCruiseDepartureList(
    departuresByDate,
    lowestDeparturePricesByCategory,
    (date) => dateFormatter(date, INTL_LONG_MONTH_LONG_YEAR),
  ), [dateFormatter, departuresByDate, lowestDeparturePricesByCategory])
  const yearOptions = useMemo(
    () => getCruiseDepartureYearOptions(departureList),
    [departureList],
  )

  useEffect(() => {
    if (selectedYear && !yearOptions.includes(selectedYear)) {
      setSelectedYear(undefined)
    }
  }, [selectedYear, yearOptions])

  const effectiveYear = selectedYear && yearOptions.includes(selectedYear) ? selectedYear : undefined
  const departureCountByYear = useMemo(
    () => getCruiseDepartureCountByYear(departureList),
    [departureList],
  )
  const departuresByMonth = useMemo(() => {
    const filteredDepartures = effectiveYear ?
        departureList.filter((item) => item.sortingData.year === effectiveYear) :
      departureList

    return groupCruiseDeparturesByMonth(sortCruiseDepartureList(filteredDepartures, sort))
  }, [departureList, effectiveYear, sort])
  const referenceDepartureDateForOccupancy = departureList[0]?.departure.departureDate

  return {
    departureCountByYear,
    departureList,
    departuresByMonth,
    hasMultipleDepartures: departureList.length > 1,
    referenceDepartureDateForOccupancy,
    selectedYear,
    setSelectedYear,
    setSort,
    sort,
    totalDepartureCount: departureList.length,
    yearOptions,
  }
}
